/* Innate enquiry rail (2026-08-07)

   Opens and closes the right-edge enquiry panel, keeps a draft in
   localStorage, and restores it when the panel is reopened or the customer
   moves to another page.

   The form itself is a real Shopify contact form, so submitting is a normal
   POST and page load — nothing here intercepts it. On the way back the URL
   carries ?enquiry_sent=true, which reopens the panel on the success message
   and clears the saved draft.

   Draft handling notes:
   - Cleared on a successful send, so a name and email never linger longer
     than the enquiry they belong to.
   - Wrapped in try/catch throughout: localStorage throws in private mode on
     some browsers, and a failed save must never stop the form working. */

(function () {
  'use strict';

  var KEY = 'innate.enquiry.draft.v1';
  var FIELDS = ['name', 'email', 'body'];

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function readDraft() {
    try { return JSON.parse(window.localStorage.getItem(KEY) || '{}'); }
    catch (e) { return {}; }
  }

  function writeDraft(data) {
    try { window.localStorage.setItem(KEY, JSON.stringify(data)); }
    catch (e) { /* private mode — the form still works, it just won't persist */ }
  }

  function clearDraft() {
    try { window.localStorage.removeItem(KEY); }
    catch (e) { /* nothing to do */ }
  }

  ready(function () {
    var rail = document.querySelector('[data-enquiry-rail]');
    if (!rail) return;

    var tab = rail.querySelector('.innate-enquiry__tab');
    var panel = rail.querySelector('.innate-enquiry__panel');
    var form = rail.querySelector('.innate-enquiry__form');
    var chips = [].slice.call(rail.querySelectorAll('[data-enquiry-chip]'));
    var subjectInput = rail.querySelector('[data-enquiry-subject-value]');
    var savedNote = rail.querySelector('[data-enquiry-saved]');
    if (!tab || !panel || !form) return;

    var inputs = {};
    FIELDS.forEach(function (f) {
      inputs[f] = form.querySelector('[name="contact[' + f + ']"]');
    });

    /* ---------- open / close ---------- */
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      panel.hidden = false;
      // The browser needs to have laid the panel out at its start position
      // before the class change, or it jumps straight to the end and the
      // slide is skipped. Reading offsetHeight forces that synchronously.
      //
      // Deliberately not requestAnimationFrame: browsers do not run rAF in a
      // background tab, which would leave the panel unhidden but stranded
      // off-screen with no way back.
      /* eslint-disable-next-line no-unused-expressions */
      panel.offsetHeight;
      rail.classList.add('is-open');
      tab.setAttribute('aria-expanded', 'true');
      var first = panel.querySelector('.innate-enquiry__chip, input, textarea');
      if (first) first.focus();
    }

    function close() {
      rail.classList.remove('is-open');
      tab.setAttribute('aria-expanded', 'false');
      // Wait for the slide-out before hiding, so the animation is visible.
      window.setTimeout(function () {
        if (!rail.classList.contains('is-open')) panel.hidden = true;
      }, 440);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      else tab.focus();
    }

    tab.addEventListener('click', open);

    rail.querySelectorAll('[data-enquiry-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && rail.classList.contains('is-open')) close();
    });

    // Clicking away closes it. The draft is already saved on every keystroke,
    // so nothing is lost.
    document.addEventListener('click', function (event) {
      if (!rail.classList.contains('is-open')) return;
      if (rail.contains(event.target)) return;
      close();
    });

    /* ---------- chips ---------- */
    function selectChip(value) {
      chips.forEach(function (chip) {
        chip.setAttribute('aria-checked', chip.dataset.enquiryChip === value ? 'true' : 'false');
      });
      if (subjectInput) subjectInput.value = value || '';
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        selectChip(chip.dataset.enquiryChip);
        save();
      });
    });

    /* ---------- draft ---------- */
    function save() {
      var data = { subject: subjectInput ? subjectInput.value : '' };
      FIELDS.forEach(function (f) { if (inputs[f]) data[f] = inputs[f].value; });
      var hasContent = Object.keys(data).some(function (k) { return data[k]; });
      if (hasContent) {
        writeDraft(data);
        if (savedNote) savedNote.hidden = false;
      } else {
        clearDraft();
        if (savedNote) savedNote.hidden = true;
      }
    }

    function restore() {
      var data = readDraft();
      if (!data || !Object.keys(data).length) return;
      FIELDS.forEach(function (f) { if (inputs[f] && data[f]) inputs[f].value = data[f]; });
      if (data.subject) selectChip(data.subject);
      if (savedNote) savedNote.hidden = false;
    }

    FIELDS.forEach(function (f) {
      if (inputs[f]) inputs[f].addEventListener('input', save);
    });

    restore();

    /* ---------- which form was actually submitted? ----------

       Shopify sets form.posted_successfully? on EVERY {% form 'contact' %} on
       the page after ANY contact submission — it does not record which one was
       posted. So the presence of a success message proves nothing about who
       sent it. That is why the rail used to slide open announcing "that's with
       us" when the customer had actually used the page's own enquiry form.

       Each contact form now stamps sessionStorage as it submits, and the stamp
       is read once on the way back. sessionStorage is the right store: it
       survives the POST and redirect, and clears with the tab. The
       ?enquiry_sent=true return_to is kept as a fallback for the case where the
       stamp never got written (storage blocked, or the script had not run). */
    var SUBMIT_KEY = 'innate.lastContactForm';

    function stamp(value) {
      try { window.sessionStorage.setItem(SUBMIT_KEY, value); } catch (e) {}
    }

    function readStamp() {
      try {
        var v = window.sessionStorage.getItem(SUBMIT_KEY);
        window.sessionStorage.removeItem(SUBMIT_KEY);
        return v;
      } catch (e) { return null; }
    }

    function contactForms() {
      return [].slice.call(document.querySelectorAll('form[action*="/contact"]'));
    }

    contactForms().forEach(function (f) {
      f.addEventListener('submit', function () {
        stamp(f === form ? 'rail' : (f.className || 'page'));
      });
    });

    /* Strip the success and error notices out of a form that did not send this
       submission, so only the form the customer actually used reports back.

       Listed explicitly rather than matched with [class*="error"]: the boardroom
       form puts an "error" class on the FIELDS themselves — the textarea carries
       class="text error" when body fails validation — so a wildcard would delete
       the customer's input controls. Add new notice classes here; do not swap
       this back for a pattern match. */
    var NOTICES = [
      '.innate-enquiry__success', '.innate-enquiry__error',
      '.contact-overhaul__form-success', '.contact-overhaul__form-errors',
      '.cp-form-success', '.cp-form-errors',
      '.hosp-form-success', '.hosp-form-errors',
      '.boardroom-form__success',
      '.d29-form-success', '.d29-form-error'
    ].join(',');

    function clearNotices(scope) {
      [].slice.call(scope.querySelectorAll(NOTICES)).forEach(function (node) {
        if (node.parentNode) { node.parentNode.removeChild(node); }
      });
    }

    /* ---------- after a send ---------- */
    var stamped = readStamp();
    var sentFlag = window.location.search.indexOf('enquiry_sent=true') !== -1;
    var railWasUsed = stamped === 'rail' || (!stamped && sentFlag);

    var railSuccess = rail.querySelector('.innate-enquiry__success');
    var railError = rail.querySelector('.innate-enquiry__error');

    if (!railWasUsed) {
      /* Somebody else's outcome — or an ordinary page load. Either way the rail
         has nothing to report and must stay shut. */
      clearNotices(form);
    } else {
      if (railSuccess) {
        clearDraft();
        FIELDS.forEach(function (f) { if (inputs[f]) inputs[f].value = ''; });
        selectChip('');
        if (savedNote) savedNote.hidden = true;
      }

      /* The rail sent it, so no in-page form should be claiming it too. */
      contactForms().forEach(function (f) {
        if (f !== form) { clearNotices(f); }
      });

      if (railSuccess || railError) { open(); }

      if (sentFlag && window.history.replaceState) {
        var clean = window.location.pathname + window.location.hash;
        window.history.replaceState(null, '', clean);
      }

      /* Fade the confirmation out after ten seconds, leaving a blank form ready
         for the next enquiry rather than a stale "sent" notice. */
      if (railSuccess) {
        window.setTimeout(function () {
          railSuccess.classList.add('is-fading');
          window.setTimeout(function () {
            if (railSuccess.parentNode) { railSuccess.parentNode.removeChild(railSuccess); }
          }, 600);
        }, 10000);
      }
    }

  });
})();
